<script lang="ts" context="module">
	declare global {
		interface Window {
			grecaptcha: {
				execute(): Promise<void>;
				reset(): void;
				getResponse(): string;
				ready(cb: () => void): void;
			};
		}
	}

	let recaptchaInserted = false;
</script>

<script lang="ts">
	import { createEventDispatcher } from "svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { handleInvalidInputs } from "./handleInvalidInputs.js";

	export let form: HTMLFormElement | null = null;
	export let id = "";

	let formClass = "";
	export { formClass as class };

	const { RECAPTCHA_SITE_KEY } = getGlobalSettings();
	const dispatch = createEventDispatcher<{ submit: SubmitEvent }>();

	let ready = false;
	async function waitForReadyRecaptcha(): Promise<void> {
		if (ready) {
			return;
		}
		await new Promise<void>((resolve) => {
			const interval = setInterval(() => {
				if (ready) {
					clearInterval(interval);
					resolve();
				}
			}, 500);
		});
	}

	async function handleAntispamSubmit(event: SubmitEvent): Promise<void> {
		event.preventDefault();

		await waitForReadyRecaptcha();
		await window.grecaptcha.execute();
		const token = window.grecaptcha.getResponse();
		if (token) {
			window.grecaptcha.reset();
			dispatch("submit", event);
		}
	}

	function recaptcha(_element: HTMLElement): void {
		async function setup(): Promise<void> {
			await new Promise<void>((resolve) => {
				const interval = setInterval(() => {
					// eslint-disable-next-line @typescript-eslint/strict-boolean-expressions, @typescript-eslint/no-unnecessary-condition
					if (window.grecaptcha) {
						clearInterval(interval);
						resolve();
					}
				}, 250);
			});
			window.grecaptcha.ready(() => {
				window.grecaptcha.reset();
				ready = true;
			});
		}

		if (recaptchaInserted) {
			void setup();
			return;
		}
		const events = ["mousemove", "click", "pointermove", "pointerdown"] as const;
		function listen(): void {
			for (const event of events) {
				window.removeEventListener(event, listen);
			}
			if (recaptchaInserted) {
				void setup();
				return;
			}
			recaptchaInserted = true;
			const script = document.createElement("script");
			script.src = "https://www.google.com/recaptcha/api.js";
			script.async = true;
			document.head.appendChild(script);
			// eslint-disable-next-line @typescript-eslint/no-misused-promises
			script.addEventListener("load", setup);
		}
		for (const event of events) {
			window.addEventListener(event, listen);
		}
	}
</script>

<form
	bind:this={form}
	class={formClass}
	{id}
	on:submit={handleAntispamSubmit}
	use:handleInvalidInputs
	on:change
	on:input
>
	<div class="g-recaptcha z-[10000]" data-sitekey={RECAPTCHA_SITE_KEY} data-size="invisible" use:recaptcha></div>

	<slot />
</form>
